import { useState } from "react";
import api from "@/lib/api";

// Same viewer/scrolling as single-person contracts; the two named confirmations replace the signature pad.
export default function DualAcceptancePanel({ row, viewerBroken, onSigned }) {
  const [confirmed, setConfirmed] = useState([false, false]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const names = row.dual_signatories || [];
  const ready = names.length === 2 && confirmed.every(Boolean) && !viewerBroken && !busy;

  async function submit() {
    if (!ready) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/portal/contracts/${row.id}/accept`, {
        dual_confirmations: confirmed,
      });
      onSigned(data);
    } catch (e) {
      // A retry after a lost response must show the completed copy, not sign again.
      try {
        const { data } = await api.get("/portal/contracts");
        const saved = data.items?.find((item) => item.id === row.id);
        if (saved?.status === "signed") { onSigned(saved); return; }
      } catch { /* Keep the original submission error. */ }
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Acceptance could not be completed. Please try again.");
    } finally { setBusy(false); }
  }

  return <section data-testid="dual-acceptance-panel">
    <h2 className="text-lg font-semibold mb-2">Accept and sign contract</h2>
    <p className="text-sm text-stone-600 mb-3">Both named people must read the contract and each tick their own confirmation below during this session.</p>
    {viewerBroken && <p className="text-sm text-red-800 mb-3" role="alert">The PDF could not be displayed. Refresh the page before signing.</p>}
    {names.map((name, index) => <label key={index} className="block border rounded-md p-3 mb-3 text-sm">
      <span className="block font-semibold mb-2">{name}</span>
      <span className="flex items-start gap-2">
        <input type="checkbox" checked={confirmed[index]} disabled={viewerBroken || busy}
          data-testid={`dual-confirm-${index + 1}`}
          onChange={(e) => setConfirmed((old) => old.map((value, i) => i === index ? e.target.checked : value))} className="mt-1" />
        <span>I, {name}, confirm that I have read this agreement, that my details are correct, and that I agree to its terms and intend this confirmation to constitute my electronic signature.</span>
      </span>
    </label>)}
    {error && <p className="text-sm text-red-800 mb-3" role="alert">{error}</p>}
    <button onClick={submit} disabled={!ready} data-testid="dual-accept-btn"
      className="w-full px-4 py-2 text-sm rounded-md bg-emerald-600 text-white disabled:opacity-50">
      {busy ? "Saving confirmations…" : "Confirm and sign contract"}
    </button>
  </section>;
}
